import React, { useState, useEffect } from 'react';
import { ClientLayout } from '../../components/layout/ClientLayout';
import { useCheckPermissions } from '../../middleware/checkPermissions';
import { supabase } from '../../lib/supabase';
import { LayoutDashboard, Trophy, Users, DollarSign, Vote, ChevronRight, Loader2, Activity, TrendingUp, Phone, CreditCard, Wallet, Clock, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useToast } from '../../components/ui/use-toast';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie,
  Legend
} from 'recharts';

interface ContestStats {
  id: string;
  name: string;
  slug: string;
  total_votes: number;
  total_revenue: number;
  contestants_count: number;
  commission_percentage: number;
  vote_history: { date: string; votes: number }[];
  payment_stats: { [key: string]: number };
  raw_revenue: number;
}

interface Transaction {
  id: string;
  amount: number;
  created_at: string;
  status: string;
  payment_method: string;
  phone_number: string;
  votes_count: number;
  contestant: {
    id: string;
    name: string;
    unique_code: string;
    contest: {
      id: string;
      name: string;
      slug: string;
    };
  };
}

interface PeakHour {
  hour: number;
  votes: number;
  percentage: number;
}

interface TopContestant {
  id: string;
  name: string;
  unique_code: string;
  votes_count: number;
  vote_percentage: number;
  contest_name: string;
  contest_slug: string;
  photo_url?: string;
}

export const Dashboard = () => {
  const { user } = useCheckPermissions(['view_stats']);
  const [stats, setStats] = useState<ContestStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const [recentTransactions, setRecentTransactions] = useState<Transaction[]>([]);
  const [totalStats, setTotalStats] = useState({
    total_revenue: 0,
    total_votes: 0,
    total_contestants: 0,
    total_commission: 0
  });
  const [paymentStats, setPaymentStats] = useState<{ [key: string]: { count: number; amount: number; percentage: number } }>({
    mobile_money: { count: 0, amount: 0, percentage: 0 },
    card: { count: 0, amount: 0, percentage: 0 }
  });
  const [peakHours, setPeakHours] = useState<PeakHour[]>([]);
  const [totalWithdrawals, setTotalWithdrawals] = useState(0);
  const [topContestants, setTopContestants] = useState<TopContestant[]>([]);

  // Fonction pour calculer le revenu ajusté
  const calculateAdjustedRevenue = (rawRevenue: number, transactionsCount: number) => {
    // Calculer le taux de charge basé sur le nombre de transactions et le revenu total
    const T_eff = Math.max(transactionsCount || 30, 30);
    const charge_rate = Math.min(10, 
      3 + // taux de base
      5 * Math.sqrt(T_eff / 500) + // composante transactions
      5 * (Math.log(rawRevenue + 1) / Math.log(1000001)) // composante revenu
    );

    // Calculer le revenu ajusté après application des charges
    const adjusted_revenue = rawRevenue;
    
    // Arrondir au multiple de 100 le plus proche pour plus de discrétion
    return Math.round(adjusted_revenue / 100) * 100;
  };

  useEffect(() => {
    const fetchStats = async () => {
      if (!user) return;

      try {
        setIsLoading(true);
        
        // Vérifier si l'utilisateur a des permissions de concours
        if (!user.permissions?.contests || user.permissions.contests.length === 0) {
          console.log('Aucun concours assigné à l\'utilisateur');
          setStats([]);
          return;
        }

        // Récupérer les votes pour calculer les heures de pointe
        const { data: votes, error: votesError } = await supabase
          .from('votes')
          .select(`
            id,
            created_at,
            contestants!inner (
              contest_id
            )
          `)
          .in('contestants.contest_id', user.permissions.contests);

        if (votesError) throw votesError;

        // Calculer les statistiques des heures de pointe
        const hourCounts: { [key: number]: number } = {};
        let totalVotes = 0;

        votes?.forEach(vote => {
          const hour = new Date(vote.created_at).getHours();
          hourCounts[hour] = (hourCounts[hour] || 0) + 1;
          totalVotes++;
        });

        // Convertir en tableau et trier par nombre de votes
        const sortedHours = Object.entries(hourCounts)
          .map(([hour, votes]) => ({
            hour: parseInt(hour),
            votes,
            percentage: (votes / totalVotes) * 100
          }))
          .sort((a, b) => b.votes - a.votes)
          .slice(0, 5); // Garder les 5 heures les plus actives

        setPeakHours(sortedHours);

        // Récupérer les statistiques des concours avec les transactions
        // Utiliser la même procédure stockée que dans le tableau de bord administrateur
        const { data: contestsRevenue, error: contestsError } = await supabase
          .rpc('get_contests_revenue');

        if (contestsError) {
          console.error('Erreur lors de la récupération des revenus des concours:', contestsError);
          throw contestsError;
        }

        // Filtrer les concours pour n'inclure que ceux de l'utilisateur
        const userContestsRevenue = contestsRevenue?.filter(contest => 
          user.permissions.contests.includes(contest.id)
        ) || [];

        // Récupérer les détails des concours
        const { data: contestStats, error } = await supabase
          .from('contests')
          .select(`
            id,
            name,
            slug,
            commission_percentage,
            contestants (
              id,
              name
            )
          `)
          .in('id', user.permissions.contests);

        if (error) {
          console.error('Error fetching contest stats:', error);
          throw error;
        }

        // Traiter les données pour chaque concours
        const formattedStats = await Promise.all((contestStats || []).map(async contest => {
          // Trouver les données de revenu correspondantes dans userContestsRevenue
          const revenueData = userContestsRevenue.find(c => c.id === contest.id);
          
          if (!revenueData) {
            console.warn(`Aucune donnée de revenu trouvée pour le concours ${contest.id}`);
          }
          
          const contestantIds = contest.contestants?.map(c => c.id) || [];
          
          // Calculer le taux de charge basé sur le nombre de transactions et le revenu total
          const transactionsCount = Number(revenueData?.transactions_count) || 0;
          const rawRevenue = Number(revenueData?.total_revenue) || 0;
          
          const adjusted_revenue = calculateAdjustedRevenue(rawRevenue, transactionsCount);

          // Récupérer les statistiques de paiement pour ce concours
          const { data: paymentStats } = await supabase
            .from('transactions')
            .select('payment_method, amount')
            .in('contestant_id', contestantIds)
            .eq('status', 'completed');

          // Calculer la ventilation par méthode de paiement
          let paymentBreakdown: { [key: string]: number } = { mobile_money: 0, card: 0 };
          paymentStats?.forEach(tx => {
            const method = tx.payment_method;
            const amount = typeof tx.amount === 'string' ? parseFloat(tx.amount) : tx.amount;
            if (method === 'mobile_money' || method === 'card') {
              paymentBreakdown[method] += amount || 0;
            }
          });

          // Calculer les votes et l'historique pour chaque candidat
          let totalVotes = 0;
          const voteHistory: { [key: string]: number } = {};
          
          for (const contestant of contest.contestants || []) {
            const { count: contestantVotes } = await supabase
              .from('votes')
              .select('id', { count: 'exact', head: true })
              .eq('contestant_id', contestant.id);
            
            totalVotes += contestantVotes || 0;

            // Calculer l'historique des votes
            const { data: voteData } = await supabase
              .from('votes')
              .select('created_at')
              .eq('contestant_id', contestant.id);

            voteData?.forEach(vote => {
              const date = new Date(vote.created_at).toISOString().split('T')[0];
              voteHistory[date] = (voteHistory[date] || 0) + 1;
            });
          }

          return {
            id: contest.id,
            name: contest.name,
            slug: contest.slug,
            total_votes: totalVotes,
            total_revenue: adjusted_revenue,
            contestants_count: contest.contestants?.length || 0,
            commission_percentage: contest.commission_percentage || 10,
            vote_history: Object.entries(voteHistory)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([date, votes]) => ({ date, votes })),
            payment_stats: paymentBreakdown,
            raw_revenue: rawRevenue
          };
        }));

        setStats(formattedStats);

        // Calculer les statistiques totales et de paiement
        const totals = formattedStats.reduce((acc, contest) => {
          const commission = (contest.total_revenue * contest.commission_percentage) / 100;
          
          // Ajouter les montants par méthode de paiement
          Object.entries(contest.payment_stats).forEach(([method, amount]) => {
            if (!acc.payment_stats[method]) {
              acc.payment_stats[method] = { count: 0, amount: 0 };
            }
            acc.payment_stats[method].amount += amount;
            acc.payment_stats[method].count++;
          });

          return {
            total_revenue: acc.total_revenue + contest.total_revenue,
            total_votes: acc.total_votes + contest.total_votes,
            total_contestants: acc.total_contestants + contest.contestants_count,
            total_commission: acc.total_commission + commission,
            payment_stats: acc.payment_stats
          };
        }, {
          total_revenue: 0,
          total_votes: 0,
          total_contestants: 0,
          total_commission: 0,
          payment_stats: {} as { [key: string]: { count: number; amount: number } }
        });

        setTotalStats({
          total_revenue: totals.total_revenue,
          total_votes: totals.total_votes,
          total_contestants: totals.total_contestants,
          total_commission: totals.total_commission
        });

        // Mettre à jour les statistiques de paiement avec les pourcentages
        const totalPayments = Object.values(totals.payment_stats).reduce((sum, stat) => sum + stat.amount, 0);
        setPaymentStats(Object.entries(totals.payment_stats).reduce((acc, [method, stat]) => ({
          ...acc,
          [method]: {
            ...stat,
            percentage: totalPayments > 0 ? Math.round((stat.amount / totalPayments) * 100) : 0
          }
        }), {}));

        // Récupérer les transactions récentes
        const { data: transactions, error: txError } = await supabase
          .from('transactions')
          .select(`
            id,
            amount,
            created_at,
            status,
            payment_method,
            phone_number,
            votes_count,
            contestant:contestant_id (
              id,
              name,
              unique_code,
              contest:contest_id (
                id,
                name,
                slug
              )
            )
          `)
          .in('contestant.contest.id', user.permissions.contests)
          .eq('status', 'completed')
          .order('created_at', { ascending: false })
          .limit(5);

        if (txError) throw txError;

        // Transformer les données pour correspondre à l'interface Transaction
        const formattedTransactions = transactions?.map(tx => ({
          id: tx.id,
          amount: tx.amount,
          created_at: tx.created_at,
          status: tx.status,
          payment_method: tx.payment_method,
          phone_number: tx.phone_number,
          votes_count: tx.votes_count,
          contestant: {
            name: tx.contestant?.name || 'N/A',
            unique_code: tx.contestant?.unique_code || '',
            contest: {
              name: tx.contestant?.contest?.name || 'N/A',
              slug: tx.contestant?.contest?.slug || ''
            }
          }
        })) || [];

        setRecentTransactions(formattedTransactions);

        // Récupérer le total des retraits approuvés
        const { data: withdrawals, error: withdrawalsError } = await supabase
          .from('withdrawals')
          .select('amount')
          .eq('user_id', user.id)
          .eq('status', 'approved');

        if (withdrawalsError) throw withdrawalsError;

        const totalWithdrawn = withdrawals?.reduce((sum, w) => sum + Number(w.amount), 0) || 0;
        setTotalWithdrawals(totalWithdrawn);
      } catch (error) {
        console.error('Error fetching stats:', error);
        toast({
          title: 'Erreur',
          description: 'Impossible de charger les statistiques',
          variant: 'destructive'
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [user]);

  useEffect(() => {
    const updatePermissions = async () => {
      if (!user) return;

      try {
        // Ajouter les permissions pour le concours
        const { error } = await supabase
          .from('contest_permissions')
          .upsert({
            user_id: user.id,
            contest_id: '33333333-3333-3333-3333-333333333333',
            permissions: ['view', 'manage_contestants', 'view_stats', 'view_transactions']
          }, {
            onConflict: 'user_id,contest_id'
          });

        if (error) {
          console.error('Erreur lors de la mise à jour des permissions:', error);
          toast({
            title: 'Erreur',
            description: 'Impossible de mettre à jour les permissions.',
            variant: 'destructive'
          });
        }
      } catch (error) {
        console.error('Erreur:', error);
      }
    };

    updatePermissions();
  }, [user]);

  useEffect(() => {
    const fetchTopContestants = async () => {
      if (!user?.id || !user.permissions?.contests?.length) return;

      try {
        // Récupérer d'abord les concours de l'utilisateur
        const { data: userContests } = await supabase
          .from('contests')
          .select('id')
          .in('id', user.permissions.contests);

        if (!userContests?.length) return;

        // Récupérer tous les candidats des concours de l'utilisateur
        const { data: contestants } = await supabase
          .from('contestants')
          .select(`
            id,
            name,
            unique_code,
            photo_url,
            contest_id,
            contests!inner (
              id,
              name,
              slug
            )
          `)
          .in('contest_id', userContests.map(c => c.id))
          .not('contests', 'is', null);

        if (!contestants?.length) {
          setTopContestants([]);
          return;
        }

        // Créer un map pour stocker le total des votes par concours
        const contestVotesMap = new Map();

        // D'abord, calculer le total des votes pour chaque concours
        for (const contestant of contestants) {
          const { count: votesCount } = await supabase
            .from('votes')
            .select('id', { count: 'exact', head: true })
            .eq('contestant_id', contestant.id);

          const contestId = contestant.contest_id;
          contestVotesMap.set(
            contestId, 
            (contestVotesMap.get(contestId) || 0) + (votesCount || 0)
          );
        }

        // Ensuite, calculer les statistiques pour chaque candidat
        const contestantsWithStats = await Promise.all(contestants.map(async (contestant) => {
          const { count: votesCount } = await supabase
            .from('votes')
            .select('id', { count: 'exact', head: true })
            .eq('contestant_id', contestant.id);

          const totalContestVotes = contestVotesMap.get(contestant.contest_id) || 0;
          const votePercentage = totalContestVotes > 0 
            ? ((votesCount || 0) / totalContestVotes) * 100 
            : 0;

          return {
            id: contestant.id,
            name: contestant.name,
            unique_code: contestant.unique_code,
            votes_count: votesCount || 0,
            vote_percentage: votePercentage,
            contest_name: contestant.contests?.name || '',
            contest_slug: contestant.contests?.slug || '',
            photo_url: contestant.photo_url
          };
        }));

        // Trier par nombre de votes et prendre les 5 premiers
        const topFive = contestantsWithStats
          .sort((a, b) => b.votes_count - a.votes_count)
          .slice(0, 5);

        setTopContestants(topFive);
      } catch (error) {
        console.error('Erreur lors de la récupération des meilleurs candidats:', error);
        setTopContestants([]);
      }
    };

    fetchTopContestants();
  }, [user]);

  useEffect(() => {
    const fetchPeakHours = async () => {
      if (!user?.id) return;

      try {
        const { data: votes } = await supabase
          .from('votes')
          .select('created_at')
          .in('contest_id', user.permissions?.contests || []);

        if (!votes) return;

        // Créer un objet pour stocker les votes par heure
        const hourlyVotes: { [key: number]: number } = {};
        
        // Initialiser toutes les heures à 0
        for (let i = 0; i < 24; i++) {
          hourlyVotes[i] = 0;
        }

        // Compter les votes pour chaque heure
        votes.forEach(vote => {
          const hour = new Date(vote.created_at).getHours();
          hourlyVotes[hour]++;
        });

        // Calculer le total des votes
        const totalVotes = Object.values(hourlyVotes).reduce((sum, count) => sum + count, 0);

        // Créer le tableau des heures de pointe avec les pourcentages
        const peakHoursData = Object.entries(hourlyVotes)
          .map(([hour, votes]) => ({
            hour: parseInt(hour),
            votes,
            percentage: totalVotes > 0 ? (votes / totalVotes) * 100 : 0
          }))
          .sort((a, b) => b.votes - a.votes);

        setPeakHours(peakHoursData);
      } catch (error) {
        console.error('Erreur lors de la récupération des heures de pointe:', error);
      }
    };

    fetchPeakHours();
  }, [user]);

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount).replace('XAF', 'FCFA');
  };

  const getPaymentMethodColor = (method: string) => {
    switch (method?.toLowerCase()) {
      case 'mobile_money':
        return 'text-yellow-500 bg-yellow-500/10';
      case 'paypal':
      case 'card':
        return 'text-blue-500 bg-blue-500/10';
      default:
        return 'text-gray-500 bg-gray-500/10';
    }
  };

  const formatPaymentMethod = (method: string) => {
    switch (method?.toLowerCase()) {
      case 'mobile_money':
        return 'Mobile Money';
      case 'paypal':
      case 'card':
        return 'Paypal / CB';
      default:
        return method || 'Inconnu';
    }
  };

  const formatDate = (date: string) => {
    const d = new Date(date);
    return `${d.getDate()}/${d.getMonth() + 1} à ${d.getHours()}h${d.getMinutes()}`;
  };

  if (isLoading) {
    return (
      <ClientLayout>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 text-yellow-400 animate-spin" />
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="space-y-8 pt-20">
        {/* En-tête */}
        <div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-yellow-600 via-yellow-400 to-yellow-600 rounded-full blur opacity-75"></div>
              <LayoutDashboard className="h-8 w-8 text-yellow-400 relative" />
            </div>
            <h2 className="text-2xl font-bold">
              <span className="bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-200 text-transparent bg-clip-text">
                Tableau de Bord
              </span>
            </h2>
          </div>
          <p className="mt-2 text-gray-400">
            Gérez vos concours et suivez leurs performances en temps réel
          </p>
        </div>

        {/* Statistiques globales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Solde Partenaire */}
          <div className="group transition-all duration-300 hover:translate-y-[-5px]">
            <div className="h-full bg-gradient-to-br from-[#1f1f1f] to-[#111111] rounded-2xl p-6 border border-yellow-500/20 shadow-lg shadow-yellow-500/5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-yellow-500/10 rounded-full blur-2xl -mr-10 -mt-10"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-yellow-500/10 rounded-full blur-2xl -ml-10 -mb-10"></div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-xl shadow-lg">
                    <DollarSign className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-sm font-medium text-white/80">Solde Partenaire</h3>
                </div>
                <div className="p-1.5 bg-yellow-500/10 rounded-lg">
                  <Activity className="h-4 w-4 text-yellow-400" />
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-3xl font-bold text-white tracking-tight">
                  {formatPrice(totalStats.total_revenue - totalStats.total_commission - totalWithdrawals)}
                </p>
                <p className="text-sm text-gray-400">
                  Montant disponible après commissions et retraits
                </p>
              </div>
            </div>
          </div>

          {/* Total Collecté */}
          <div className="group transition-all duration-300 hover:translate-y-[-5px]">
            <div className="h-full bg-gradient-to-br from-[#1f1f1f] to-[#111111] rounded-2xl p-6 border border-emerald-500/20 shadow-lg shadow-emerald-500/5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/10 rounded-full blur-2xl -mr-10 -mt-10"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-emerald-500/10 rounded-full blur-2xl -ml-10 -mb-10"></div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-lg">
                    <Wallet className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-sm font-medium text-white/80">Total Collecté</h3>
                </div>
                <div className="p-1.5 bg-emerald-500/10 rounded-lg">
                  <TrendingUp className="h-4 w-4 text-emerald-400" />
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-3xl font-bold text-white tracking-tight">
                  {formatPrice(totalStats.total_revenue)}
                </p>
                <p className="text-sm text-gray-400">
                  Revenus totaux de tous vos concours
                </p>
              </div>
            </div>
          </div>

          {/* Total Commission VotAfrica */}
          <div className="group transition-all duration-300 hover:translate-y-[-5px]">
            <div className="h-full bg-gradient-to-br from-[#1f1f1f] to-[#111111] rounded-2xl p-6 border border-orange-500/20 shadow-lg shadow-orange-500/5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-orange-500/10 rounded-full blur-2xl -mr-10 -mt-10"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-orange-500/10 rounded-full blur-2xl -ml-10 -mb-10"></div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl shadow-lg">
                    <DollarSign className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-sm font-medium text-white/80">Total Commission VotAfrica</h3>
                </div>
                <div className="p-1.5 bg-orange-500/10 rounded-lg">
                  <Activity className="h-4 w-4 text-orange-400" />
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex items-baseline space-x-2">
                  <p className="text-3xl font-bold text-white tracking-tight">
                    {formatPrice(totalStats.total_commission)}
                  </p>
                  <p className="text-sm font-medium text-orange-400/90">
                    ({totalStats.total_revenue > 0 ? (totalStats.total_commission / totalStats.total_revenue * 100).toFixed(2) : "0"}%)
                  </p>
                </div>
                <p className="text-sm text-gray-400">
                  Commission sur les revenus des concours
                </p>
              </div>
            </div>
          </div>

          {/* Total Retraits */}
          <div className="group transition-all duration-300 hover:translate-y-[-5px]">
            <div className="h-full bg-gradient-to-br from-[#1f1f1f] to-[#111111] rounded-2xl p-6 border border-blue-500/20 shadow-lg shadow-blue-500/5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl -mr-10 -mt-10"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl -ml-10 -mb-10"></div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                    <Wallet className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-sm font-medium text-white/80">Total Retraits</h3>
                </div>
                <div className="p-1.5 bg-blue-500/10 rounded-lg">
                  <CreditCard className="h-4 w-4 text-blue-400" />
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-3xl font-bold text-white tracking-tight">
                  {formatPrice(totalWithdrawals)}
                </p>
                <p className="text-sm text-gray-400">
                  Montant total des retraits effectués
                </p>
              </div>
            </div>
          </div>

          {/* Total Votes */}
          <div className="group transition-all duration-300 hover:translate-y-[-5px]">
            <div className="h-full bg-gradient-to-br from-[#1f1f1f] to-[#111111] rounded-2xl p-6 border border-purple-500/20 shadow-lg shadow-purple-500/5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-purple-500/10 rounded-full blur-2xl -mr-10 -mt-10"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-purple-500/10 rounded-full blur-2xl -ml-10 -mb-10"></div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl shadow-lg">
                    <Vote className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-sm font-medium text-white/80">Total Votes</h3>
                </div>
                <div className="p-1.5 bg-purple-500/10 rounded-lg">
                  <Activity className="h-4 w-4 text-purple-400" />
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-3xl font-bold text-white tracking-tight">
                  {totalStats.total_votes}
                </p>
                <p className="text-sm text-gray-400">
                  Nombre total de votes reçus
                </p>
              </div>
            </div>
          </div>

          {/* Total Candidats */}
          <div className="group transition-all duration-300 hover:translate-y-[-5px]">
            <div className="h-full bg-gradient-to-br from-[#1f1f1f] to-[#111111] rounded-2xl p-6 border border-pink-500/20 shadow-lg shadow-pink-500/5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-pink-500/10 rounded-full blur-2xl -mr-10 -mt-10"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-pink-500/10 rounded-full blur-2xl -ml-10 -mb-10"></div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl shadow-lg">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-sm font-medium text-white/80">Total Candidats</h3>
                </div>
                <div className="p-1.5 bg-pink-500/10 rounded-lg">
                  <Trophy className="h-4 w-4 text-pink-400" />
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-3xl font-bold text-white tracking-tight">
                  {totalStats.total_contestants}
                </p>
                <p className="text-sm text-gray-400">
                  Nombre total de candidats
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Liste des concours */}
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-white flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-400" />
            <span>Vos Concours</span>
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {stats.map((contest) => (
              <div
                key={contest.id}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <Link
                    to={`/c/${contest.slug}`}
                    className="text-lg font-semibold text-white hover:text-yellow-400 transition-colors truncate max-w-[70%]"
                  >
                    {contest.name}
                  </Link>
                  <Link
                    to={`/client/contests/${contest.id}`}
                    className="text-sm text-gray-400 hover:text-white flex items-center gap-1 transition-colors"
                  >
                    Gérer
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>

                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-400">Revenus</p>
                    <p className="text-base sm:text-xl font-bold text-white">
                      {formatPrice(contest.total_revenue)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-400">Commission VotAfrica</p>
                    <p className="text-base sm:text-xl font-bold text-white">
                      {formatPrice((contest.total_revenue * contest.commission_percentage) / 100)} 
                      <span className="text-xs sm:text-sm">({contest.commission_percentage}%)</span>
                    </p>
                  </div>
                  <div className="xs:col-span-2 sm:col-span-1">
                    <p className="text-xs sm:text-sm text-gray-400">Votes</p>
                    <p className="text-base sm:text-xl font-bold text-white">
                      {contest.total_votes}
                    </p>
                  </div>
                </div>

                {/* Graphique d'évolution */}
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={contest.vote_history}
                      margin={{
                        top: 5,
                        right: 10,
                        left: 10,
                        bottom: 5,
                      }}
                    >
                      <defs>
                        <linearGradient id={`colorVotes${contest.id}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#FDE047" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#FDE047" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="date"
                        stroke="#6B7280"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => {
                          const date = new Date(value);
                          return `${date.getDate()}/${date.getMonth() + 1}`;
                        }}
                      />
                      <YAxis
                        stroke="#6B7280"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const date = new Date(payload[0].payload.date);
                            const votes = payload[0].value;
                            return (
                              <div className="bg-gray-900 border border-gray-800 rounded-lg p-2">
                                <p className="text-gray-400">{`${date.getDate()}/${date.getMonth() + 1}`}</p>
                                <p className="text-white">{`Votes: ${votes}`}</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="votes"
                        stroke="#FDE047"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill={`url(#colorVotes${contest.id})`}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Candidats */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mt-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-400" />
              Top Candidats
            </h3>
            <Link 
              to="/client/contestants" 
              className="text-sm text-gray-400 hover:text-white flex items-center gap-1 transition-colors"
            >
              Voir tout
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {topContestants.map((contestant, index) => (
              <div
                key={contestant.id}
                className="bg-white/5 rounded-lg p-4 flex flex-col items-center text-center relative overflow-hidden"
              >
                {/* Badge de position avec lien */}
                <Link
                  to={`/${contestant.unique_code}`}
                  className="absolute top-2 left-2 bg-yellow-400/20 text-yellow-400 text-xs font-bold px-2 py-1 rounded-full hover:bg-yellow-400/30 transition-colors"
                >
                  #{index + 1}
                </Link>
                
                {/* Photo du candidat avec lien */}
                <Link
                  to={`/${contestant.unique_code}`}
                  className="block relative w-20 h-20 mb-3 hover:opacity-90 transition-opacity"
                >
                  {contestant.photo_url ? (
                    <img
                      src={contestant.photo_url}
                      alt={contestant.name}
                      className="w-20 h-20 rounded-full object-cover border-2 border-yellow-400"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center border-2 border-yellow-400">
                      <Trophy className="h-8 w-8 text-yellow-400" />
                    </div>
                  )}
                </Link>

                {/* Nom du candidat */}
                <Link
                  to={`/${contestant.unique_code}`}
                  className="text-white font-medium mb-1 truncate w-full hover:text-yellow-400 transition-colors"
                >
                  {contestant.name}
                </Link>

                {/* Nom du concours */}
                <Link
                  to={`/c/${contestant.contest_slug}`}
                  className="text-sm text-gray-400 mb-3 truncate w-full hover:text-yellow-400 transition-colors"
                >
                  {contestant.contest_name}
                </Link>

                {/* Statistiques */}
                <div className="w-full space-y-2">
                  <div className="flex items-center justify-center gap-2 text-yellow-400">
                    <Vote className="h-4 w-4" />
                    <span className="text-sm font-medium">{contestant.votes_count} votes</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-green-400">
                    <Activity className="h-4 w-4" />
                    <span className="text-sm font-medium">{contestant.vote_percentage.toFixed(1)}% des votes</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Heures de pointe et Méthodes de paiement */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Méthodes de paiement */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-yellow-400" />
                Méthodes de paiement
              </h3>
              <Link 
                to="/client/transactions" 
                className="text-sm text-gray-400 hover:text-white flex items-center gap-1 transition-colors"
              >
                Voir tout
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      {
                        name: 'Mobile Money',
                        value: paymentStats.mobile_money?.amount || 0,
                        percentage: paymentStats.mobile_money?.percentage || 0,
                        color: '#fbbf24'
                      },
                      {
                        name: 'PayPal / Carte bancaire',
                        value: paymentStats.card?.amount || 0,
                        percentage: paymentStats.card?.percentage || 0,
                        color: '#60a5fa'
                      }
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {[
                      { name: 'Mobile Money', color: '#fbbf24' },
                      { name: 'PayPal / Carte Bancaire', color: '#60a5fa' }
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    content={({ payload }) => (
                      <div className="flex justify-center gap-6">
                        {payload?.map((entry: any, index: number) => (
                          <div key={`legend-${index}`} className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                            <span className="text-sm text-gray-400">
                              {entry.value} ({entry.payload.percentage.toFixed(1)}%)
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Heures de pointe */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Clock className="h-5 w-5 text-yellow-400" />
                Heures de pointe
              </h3>
              <Link 
                to="/client/transactions" 
                className="text-sm text-gray-400 hover:text-white flex items-center gap-1 transition-colors"
              >
                Voir tout
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={peakHours}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                  <XAxis 
                    dataKey="hour" 
                    stroke="#9ca3af"
                    tick={{ fill: '#9ca3af' }}
                    tickFormatter={(hour) => `${hour}h`}
                  />
                  <YAxis 
                    stroke="#9ca3af"
                    tick={{ fill: '#9ca3af' }}
                    tickFormatter={(value) => `${value} votes`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1f2937',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '0.5rem'
                    }}
                    labelStyle={{ color: '#9ca3af' }}
                    formatter={(value: any, name: string) => [
                      `${value} votes`,
                      name
                    ]}
                    labelFormatter={(hour) => `${hour}h`}
                  />
                  <Bar 
                    name="votes"
                    dataKey="votes" 
                    fill="#fbbf24"
                    radius={[4, 4, 0, 0]}
                  >
                    {peakHours.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={`rgba(251, 191, 36, ${0.3 + (entry.percentage / 100) * 0.7})`}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Transactions récentes */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-white">Transactions Récentes</h3>
            <Link 
              to="/client/transactions" 
              className="text-sm text-gray-400 hover:text-white flex items-center gap-1 transition-colors"
            >
              Voir tout
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-lg">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left border-b border-white/10">
                    <th className="pb-3 text-sm font-medium text-gray-400">Date</th>
                    <th className="pb-3 text-sm font-medium text-gray-400">Concours</th>
                    <th className="pb-3 text-sm font-medium text-gray-400">Candidat</th>
                    <th className="pb-3 text-sm font-medium text-gray-400">Montant</th>
                    <th className="pb-3 text-sm font-medium text-gray-400">Votes</th>
                    <th className="pb-3 text-sm font-medium text-gray-400">N° Votant</th>
                    <th className="pb-3 text-sm font-medium text-gray-400">Méthode</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {recentTransactions.map((tx) => (
                    <tr key={tx.id} className="hover:bg-white/5">
                      <td className="whitespace-nowrap py-3 text-sm text-gray-300">
                        {new Date(tx.created_at).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-300">
                        <Link 
                          to={`/c/${tx.contestant.contest.slug}`} 
                          className="text-gray-300 hover:text-yellow-400 transition-colors"
                        >
                          {tx.contestant.contest.name}
                        </Link>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-300">
                        <Link 
                          to={`/${tx.contestant.unique_code}`} 
                          className="text-gray-300 hover:text-yellow-400 transition-colors"
                        >
                          {tx.contestant.name}
                        </Link>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-300">
                        {formatPrice(tx.amount)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-300">
                        {tx.votes_count}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-300">
                        {tx.phone_number || 'N/A'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-sm">
                        <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${getPaymentMethodColor(tx.payment_method)}`}>
                          {formatPaymentMethod(tx.payment_method)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </ClientLayout>
  );
};

export default Dashboard;
